import { parseISO } from 'date-fns';
import { StatusCell, BarCell, PointGroupCell } from './AnalyticsTableCells';
import LabelWithTooltip from '../../LabelWithTooltip/LabelWithTooltip';
import { MOTOR_STATUS_LABELS, SWITCHGEAR_STATUS_LABELS } from './constants';
import { formatSiteDatetime } from '../../../datetimeUtils';
import {
  SwitchgearCounterClosedIcon,
  SwitchgearCounterOpenIcon,
} from '../../SiteAdminView/AnalyticsView/components/SwitchgearCounter/SwitchgearCounterIcons';

const formatOperationTime = (value, timezone) => {
  if (!value || !timezone) return value || '–';
  try {
    return formatSiteDatetime(parseISO(value), timezone);
  } catch {
    return value;
  }
};

const getMax = (data, field) => {
  if (!data?.length) return 1;
  return Math.max(...data.map((row) => row[field] ?? 0)) || 1;
};

const statusColumn = {
  id: 'status',
  Header: 'Status',
  headerTooltip: 'Status',
  accessor: 'status',
  sortable: true,
  minWidth: 10,
  Cell: StatusCell,
};

const motorStatusColumn = {
  ...statusColumn,
  statusLabels: MOTOR_STATUS_LABELS,
};

export const getSwitchgearCounterColumns = (
  data = [],
  format,
  additionalColumns = [],
  timezone
) => {
  const narrow = format === 'narrow';
  const allColumns = [
    {
      ...statusColumn,
      width: 55,
      statusLabels: SWITCHGEAR_STATUS_LABELS,
      statusIcons: {
        ok: SwitchgearCounterOpenIcon,
        unknown: SwitchgearCounterClosedIcon,
      },
    },
    {
      id: 'point_group',
      Header: 'Point Group',
      headerTooltip: 'Point Group',
      accessor: 'point_group',
      sortable: true,
      width: narrow ? 90 : 180,
      minWidth: 60,
      Cell: PointGroupCell,
    },
    {
      id: 'phase',
      Header: 'Phase',
      headerTooltip: 'Phase',
      accessor: 'phase',
      sortable: true,
      width: narrow ? 50 : 70,
      minWidth: 40,
      Cell: ({ cell }) => {
        const phaseLabels = { 1: 'L1', 2: 'L2', 3: 'L3' };
        return <LabelWithTooltip name={phaseLabels[cell.value] ?? '\u2013'} />;
      },
    },
    {
      id: 'operation_count',
      Header: narrow ? 'Op Count' : 'Operation Count',
      headerTooltip: 'Operation Count',
      accessor: 'operation_count',
      sortable: true,
      width: narrow ? 70 : 140,
      minWidth: 40,
      maxValue: getMax(data, 'operation_count'),
      ...(narrow ? { Cell: ({ cell }) => cell.value ?? 0 } : { Cell: BarCell }),
    },
    {
      id: 'last_operation_time',
      Header: narrow ? 'Last Op Time' : 'Last Operation Time',
      headerTooltip: 'Last Operation Time',
      accessor: 'last_operation_time',
      sortable: true,
      width: narrow ? 100 : 100,
      minWidth: 40,
      Cell: ({ cell }) => <LabelWithTooltip name={formatOperationTime(cell.value, timezone)} />,
    },
    {
      id: 'days_since_last_operation',
      Header: narrow ? 'Days Since Last Op' : 'Days Since Last Operation',
      headerTooltip: 'Days Since Last Operation',
      accessor: 'days_since_last_operation',
      sortable: true,
      width: narrow ? 80 : 200,
      minWidth: 40,
      maxValue: getMax(data, 'days_since_last_operation'),
      ...(narrow ? { Cell: ({ cell }) => cell.value ?? 0 } : { Cell: BarCell }),
    },
  ];
  return allColumns.filter((col) => !col.optional || additionalColumns.includes(col.id));
};

export const getMotorRuntimeColumns = (data = [], format) => {
  const narrow = format === 'narrow';
  return [
    motorStatusColumn,
    {
      id: 'point_group',
      Header: 'Point Group',
      headerTooltip: 'Point Group',
      accessor: 'point_group',
      sortable: true,
      width: narrow ? 90 : 180,
      minWidth: 60,
      Cell: PointGroupCell,
    },
    {
      id: 'number_of_starts',
      Header: narrow ? '#Starts' : 'Number of Starts',
      headerTooltip: 'Number of Starts',
      accessor: 'number_of_starts',
      sortable: true,
      width: narrow ? 60 : 140,
      minWidth: 40,
      maxValue: getMax(data, 'number_of_starts'),
      ...(narrow ? { Cell: ({ cell }) => cell.value ?? 0 } : { Cell: BarCell }),
    },
    {
      id: 'starts_per_day',
      Header: narrow ? '#Starts/day' : 'Starts per Day',
      headerTooltip: 'Starts per Day',
      accessor: 'starts_per_day',
      sortable: true,
      width: narrow ? 70 : 90,
      minWidth: 40,
    },
    {
      id: 'run_time_hours',
      Header: narrow ? 'Time(h)' : 'Run Time (hours)',
      headerTooltip: 'Run Time (hours)',
      accessor: 'run_time_hours',
      sortable: true,
      width: narrow ? 60 : 150,
      minWidth: 40,
      maxValue: getMax(data, 'run_time_hours'),
      ...(narrow ? { Cell: ({ cell }) => cell.value ?? 0 } : { Cell: BarCell }),
    },
    {
      id: 'run_time_percent',
      Header: narrow ? 'Time(%)' : 'Run Time (%)',
      headerTooltip: 'Run Time (%)',
      accessor: 'run_time_percent',
      sortable: true,
      width: narrow ? 60 : 120,
      minWidth: 40,
      Cell: ({ cell }) => `${cell.value ?? 0}%`,
    },
  ];
};

export const getColumnsForTableType = (
  tableType,
  data = [],
  format,
  additionalColumns = [],
  timezone
) => {
  switch (tableType) {
    case 'motor-runtime': {
      return getMotorRuntimeColumns(data, format);
    }
    default: {
      return getSwitchgearCounterColumns(data, format, additionalColumns, timezone);
    }
  }
};
